// About, help, guidelines and legal pages. Legal texts are DRAFTS for a lawyer to check before launch
// (docs/03-PLAN.md, "Legal risks to bring to the lawyer"). Placeholders in [brackets] must be filled by Hishi.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Rainbow } from '../components/Bits';

function Doc({ line, title, draft, children }: { line: string; title: ReactNode; draft?: boolean; children: ReactNode }) {
  return (
    <article className="narrow col doc">
      <div className="hero"><span className="line">{line}</span><h1>{title}</h1></div>
      {draft && <p className="banner">Draft. This text has not been checked by a lawyer yet and is not in force.</p>}
      {children}
    </article>
  );
}

export function About() {
  return (
    <Doc line="About" title={<>A better internet, <Rainbow text="not a time machine" />.</>}>
      <p>Nostalgia is a social network where every memory is filed under the year it is about, from 1995 to right now. You can share memories, start #hashtags, ideas and communities, help people find lost things, and pass on kindness.</p>
      <p>Feeds end. "I remember this" counts shared experience, not popularity. There is always a plain time-order feed, and we explain how the other feeds are sorted.</p>
      <p>No AI-generated images, no fake activity, no selling your data. Photos are never used to train AI.</p>
    </Doc>
  );
}

export function Help() {
  return (
    <Doc line="Help" title={<>How Nostalgia <Rainbow text="works" /></>}>
      <h2 className="h2">Posting</h2>
      <p>Name the thing, write what you remember, and pick the year it is about. Tick "I lived this" or "Before my time", and "Core memory" if it really matters to you.</p>
      <h2 className="h2">Hashtags</h2>
      <p>Write # and a word anywhere in a memory, a comment or an idea, for example #snowday. If nobody used it before, you just started a new hashtag. Everyone can open it, follow it and add to it.</p>
      <h2 className="h2">Ideas</h2>
      <p>An idea is a question for everyone, like "What was your first ringtone?". People answer with memories. Start one on the <Link to="/ideas">Ideas</Link> page.</p>
      <h2 className="h2">Communities</h2>
      <p>Anyone can start a community with its own rules. Join to post in it.</p>
      <h2 className="h2">Forgotten Forever?</h2>
      <p>Can't remember what something was called? Describe it and tick "I don't know what this is". When someone finds it, mark it as found.</p>
      <h2 className="h2">Kindness and Pass it on</h2>
      <p>Share a kind thing someone did, or that you did. Read one, tap Pass it on, and share your own kind act linked to it.</p>
      <h2 className="h2">Safety</h2>
      <p>Every memory and comment has Report. Anyone, even without an account, can ask us to remove a post about them. See the <Link to="/guidelines">guidelines</Link>.</p>
      <h2 className="h2">Contact</h2>
      <p>Email: <b>[contact email]</b>. We answer in German or English.</p>
    </Doc>
  );
}

export function Guidelines() {
  return (
    <Doc line="Community guidelines" title={<>Be kind to <Rainbow text="the past" /> and the people in it.</>}>
      <ul className="rules-list">
        <li><b>Real memories only.</b> No AI-generated images, no made-up stories presented as real.</li>
        <li><b>Protect other people.</b> Don't post full names, addresses or schools of people from your past who aren't here. No photos of other people without their OK, and never photos of other people's children.</li>
        <li><b>No sensitive details about others.</b> Health, sexuality, religion, crimes: share your own story, not someone else's.</li>
        <li><b>No bullying, hate, threats or sexual content.</b> No content involving minors in any sexual way, ever.</li>
        <li><b>Your own content.</b> Only upload photos and text you have the right to share.</li>
        <li><b>No spam, no ads, no fake accounts.</b></li>
        <li><b>Name the thing.</b> Give memories a title that says what they are about, so others can find them.</li>
        <li><b>Hard memories are allowed.</b> Not every year was good. Be gentle with people who share those.</li>
      </ul>
      <h2 className="h2">What happens when you report</h2>
      <p>A person reviews it. If we remove a post or limit an account, the person affected gets a short reason and can ask us to look again. If someone's life or safety is in danger, we contact the police.</p>
    </Doc>
  );
}

export function Privacy() {
  return (
    <Doc draft line="Privacy" title={<>Your <Rainbow text="privacy" /></>}>
      <p><b>Who is responsible:</b> [full name], [street address], Switzerland, [email]. EU representative: [to be appointed before launch in the EU].</p>
      <h2 className="h2">What we collect</h2>
      <ul><li>Your account: email, username, display name, colour, optional bio and away message.</li>
        <li>Your age group at sign-up, to apply the rules for users under 16. For 13 to 15 year olds, a parent's email for consent.</li>
        <li>What you post: memories, comments, reactions, saves, follows, communities, ideas, reports.</li>
        <li>Technical data needed to run the service and keep it safe (for example, log entries for a few days).</li></ul>
      <h2 className="h2">What we don't do</h2>
      <ul><li>No advertising trackers, no selling data, no AI training on your photos or posts.</li>
        <li>No tracking cookies. We only store what is needed to keep you signed in, so there is no cookie banner.</li>
        <li>Visitor numbers: we use Cloudflare Web Analytics, which counts page views without cookies, without storing your IP address and without building a profile of you.</li></ul>
      <h2 className="h2">Where data is stored</h2>
      <p>On servers in the EU (Frankfurt) run by our database provider, under a data processing agreement. Emails are sent through an email provider under the same kind of agreement.</p>
      <h2 className="h2">Your rights</h2>
      <p>You can see, download and delete your data at any time in Settings. You can also write to [email]. If someone posted about you, you can ask us to remove it, even without an account. You can complain to the Swiss FDPIC or, in the EU, to your data protection authority.</p>
      <h2 className="h2">How long we keep it</h2>
      <p>Until you delete it or your account. Deleted content is removed from backups within [30] days.</p>
      <p className="small muted">In this preview nothing is sent anywhere: everything stays in your browser.</p>
    </Doc>
  );
}

export function Terms() {
  return (
    <Doc draft line="Terms" title={<>The <Rainbow text="rules" /> of the house</>}>
      <p>By using Nostalgia you agree to these terms and the <Link to="/guidelines">community guidelines</Link>.</p>
      <h2 className="h2">Who can join</h2><p>You must be 13 or older. If you are 13 to 15, a parent must agree by email before you can post.</p>
      <h2 className="h2">Your content</h2><p>You keep the rights to what you post. You allow us to show it on Nostalgia to the people you chose. You confirm you have the right to share it.</p>
      <h2 className="h2">Moderation</h2><p>People review reports. We may remove content or limit accounts that break the rules or the law. When we do, we tell you why and how to ask for a second look. We use automatic tools only for spam limits, and say so when we do.</p>
      <h2 className="h2">Ending</h2><p>You can delete your account at any time in Settings. We may close accounts that seriously or repeatedly break the rules.</p>
      <h2 className="h2">Changes</h2><p>We tell you before important changes to these terms.</p>
      <h2 className="h2">Law</h2><p>Swiss law applies, without taking away rights you have under the law of your country.</p>
    </Doc>
  );
}

export function Impressum() {
  return (
    <Doc draft line="Impressum" title="Impressum">
      <p>Nostalgia is run by:</p>
      <p><b>[Full legal name]</b><br />[Street and number]<br />[Postcode, town]<br />Switzerland</p>
      <p>Email: [email]<br />Contact form: [link, coming with launch]</p>
      <p className="small muted">Required by Swiss law (UWG Art. 3 para. 1 lit. s). A street address is needed; a P.O. box is not enough. To be checked by a lawyer before launch.</p>
    </Doc>
  );
}

export function NotFound({ what }: { what?: string }) {
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">404</span><h1>This page is <Rainbow text="lost to time" />.</h1><p>{what ?? 'Maybe it never existed, maybe the internet buried it.'}</p></div>
      <div className="row"><Link className="btn hot" to="/">Back to now</Link><Link className="btn ghost" to="/forgotten">Help find lost things</Link></div>
    </div>
  );
}
